import React, { useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { isValidPhoneNumber } from "react-phone-number-input";
import { GET_ACTIVE_USER } from "../../../queries/auth.queries";
import { UPDATE_USER } from "../../../queries/user.queries";
import { ErrorHandlingContext } from "../../../context/error-handling.context";

interface ActiveCustomer {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
}

interface ActiveUserData {
  activeCustomer?: ActiveCustomer | null;
}

interface UpdateUserVariables {
  firstName: string;
  lastName: string;
}

const NAME_SAVE_DEBOUNCE_MS = 800;

const splitFullName = (fullName: string): UpdateUserVariables => {
  const firstSpaceIndex = fullName.indexOf(" ");

  if (firstSpaceIndex === -1) {
    return { firstName: fullName, lastName: "" };
  }

  return {
    firstName: fullName.slice(0, firstSpaceIndex),
    lastName: fullName.slice(firstSpaceIndex + 1).trim(),
  };
};

export const useAccountPersonalInfo = () => {
  const [phoneValue, setPhoneValue] = useState("");
  const [fullnameValue, setFullnameValue] = useState("");
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);

  const { setErrorHandlingState } = useContext(ErrorHandlingContext) as unknown as {
    setErrorHandlingState: (state: { showError: boolean; data: string }) => void;
  };

  const { data: userData, loading } = useQuery<ActiveUserData>(GET_ACTIVE_USER);
  const activeCustomer = userData?.activeCustomer;

  const [updateUserFullname, { error: updateUserNameError }] =
    useMutation<unknown, UpdateUserVariables>(UPDATE_USER, {
      refetchQueries: [{ query: GET_ACTIVE_USER }],
      awaitRefetchQueries: true,
    });

  useEffect(() => {
    if (!updateUserNameError?.message) return;

    setErrorHandlingState({
      showError: true,
      data: updateUserNameError.message,
    });
  }, [updateUserNameError, setErrorHandlingState]);

  // Hydrate the fields from the server exactly once. Re-running this on every
  // `activeCustomer` change (e.g. the refetch after the debounced name autosave
  // below, or after Security's email/password mutations, which share this same
  // query) would clobber whatever the user has typed since that refetch was
  // kicked off — a fast type-then-delete could see their edit briefly replaced
  // by the now-stale value that was in flight.
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!activeCustomer || hasHydratedRef.current) return;
    hasHydratedRef.current = true;

    setPhoneValue(activeCustomer.phoneNumber ?? "");
    setFullnameValue(
      activeCustomer.firstName?.trim()
        ? `${activeCustomer.firstName} ${activeCustomer.lastName ?? ""}`.trim()
        : ""
    );
  }, [activeCustomer]);

  const savedFullName = activeCustomer
    ? [activeCustomer.firstName, activeCustomer.lastName].filter(Boolean).join(" ")
    : "";

  // Kept in a ref (rather than an effect dependency) so the debounce timer
  // below is driven purely by keystrokes and never re-triggered by Apollo's
  // async loading/refetch state changing in the background.
  const savedFullNameRef = useRef(savedFullName);
  savedFullNameRef.current = savedFullName;

  const nameSaveTimeoutRef = useRef<number>();

  useEffect(
    () => () => {
      if (nameSaveTimeoutRef.current) {
        window.clearTimeout(nameSaveTimeoutRef.current);
      }
    },
    []
  );

  const savedPhoneNumber = activeCustomer?.phoneNumber ?? "";
  const isPhoneEditable =
    isValidPhoneNumber(`${phoneValue}`) && phoneValue !== savedPhoneNumber;

  // Auto-save the name a beat after the user stops typing, instead of
  // requiring an explicit "Edit"/save action.
  const onFullnameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFullnameValue(value);

    if (nameSaveTimeoutRef.current) {
      window.clearTimeout(nameSaveTimeoutRef.current);
    }

    nameSaveTimeoutRef.current = window.setTimeout(() => {
      // Guard the mutation, not the field: a blank name is never sent to the
      // backend (it would break greetings, order confirmations, delivery
      // labels, etc.), but the input is left exactly as the user typed it.
      if (!value || value === savedFullNameRef.current) return;

      updateUserFullname({ variables: splitFullName(value) }).catch((err) =>
        console.error(err)
      );
    }, NAME_SAVE_DEBOUNCE_MS);
  };

  const onEditPhone = () => {
    if (!isPhoneEditable) return;
    setIsVerifyModalOpen(true);
  };

  const closeVerifyModal = () => setIsVerifyModalOpen(false);

  return {
    loading,
    phoneValue,
    setPhoneValue,
    fullnameValue,
    onFullnameChange,
    onEditPhone,
    isPhoneEditable,
    isVerifyModalOpen,
    closeVerifyModal,
  };
};
